import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { useCollectionEntity } from '@utilities/hooks/useCollectionEntity/useCollectionEntity';
import { Unit } from '@data/unit/Unit';
import { CaseDetailsInfoSelector, CaseDetailsAppointmentSelector, CaseDetailsPerformerSelector } from '@store/selectors/CaseDetailsSelector';
import { RevisionStatus } from '@interfaces/SurgeryRequest/RevisionStatusEnum';
import { ENDPOINT_USER_UNITS } from '@utilities/apiConstants';
import { parseAsUtcDate } from '@utilities/dateUtilities';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

export const useGetRevisionInfo = (revisionStatus: RevisionStatus) => {
	const {
		unit,
		revisions,
	} = useSelector(CaseDetailsInfoSelector);

	const {
		procedureDate,
	} = useSelector(CaseDetailsAppointmentSelector);

	const {
		firstName,
		lastName,
	} = useSelector(CaseDetailsPerformerSelector);
	const { hospitalTimeZone } = useCollectionEntity<Unit>(ENDPOINT_USER_UNITS, unit, 'id');

	const name = `${firstName} ${lastName}`;
	const parsedProcedureDate = procedureDate ? new Date(procedureDate) : null;
	const requestedAppointmentDate = parsedProcedureDate
		? format(parsedProcedureDate, 'MM/dd/yyyy')
		: '';
	const requestedAppointmentTime = parsedProcedureDate
		? format(parsedProcedureDate, 'h:mm a')
		: '';

	const { hospitalName, name: unitName } = useCollectionEntity<Unit>(ENDPOINT_USER_UNITS, unit, 'id');
	const locationDisplay = `${hospitalName} ${unitName}`;

	const filteredRevisions = useMemo(
		() => (revisions || []).filter((rev) => rev.status === revisionStatus),
		[revisionStatus, revisions]
	);
	const { metaData, dateCreated } = filteredRevisions[0] || {};
	const parsedDateCreated = dateCreated ? new Date(dateCreated + 'Z') : null;
	const dateCreatedDisplay = parsedDateCreated
		? format(parsedDateCreated, 'h:mm a MMM d')
		: '';
	const { previousAppointmentDateTime, previousFIN } = metaData || {};
	const previousDateTimeZoned = previousAppointmentDateTime && hospitalTimeZone
		? toZonedTime(parseAsUtcDate(previousAppointmentDateTime), hospitalTimeZone)
		: null;
	const previousDisplayDate = previousDateTimeZoned
		? format(previousDateTimeZoned, 'MM/dd/yyyy')
		: '';
	const previousDisplayTime = previousDateTimeZoned
		? format(previousDateTimeZoned, 'h:mm a')
		: '';

	return {
		unit,
		locationDisplay,
		dateCreated,
		revisions,
		filteredRevisions,
		procedureDate,
		requestedAppointmentDate,
		requestedAppointmentTime,
		dateCreatedDisplay,
		name,
		previousFIN,
		previousDisplayDate,
		previousDisplayTime,
	};
};
